import { EGG_FREEZING_PRE_CYCLE_SECTION_IDS } from "@src/packages/homepage/experience";
import { useTranslations } from "@src/packages/i18n";
import { getJourneyStepCompletionId } from "../completion";
import type { PlanAction, PlanStep } from "../plan.types";
import { EggFreezingBiologyTimelineStepSectionCard } from "./EggFreezingBiologyDeepCarouselCard";
import { EggFreezingKnowYourNumbersTimelineStepSectionCard } from "./EggFreezingKnowYourNumbersCard";
import {
  EggFreezingPrepTimelineRemainingSteps,
  EggFreezingPrepTimelineTransitionCards,
} from "./EggFreezingPrepTimelineRemainingSteps";
import { PreCycleHome, type PreCycleHomeSection } from "./PreCycleHome";
import type { PreCyclePrepStep } from "./PreCycleHeroCard";

type Props = {
  activeStep: PlanStep | null;
  steps: PreCyclePrepStep[];
  completedSteps: number;
  totalSteps: number;
  userAge?: number;
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
};

export function PreCycleEggFreezingHome({
  activeStep,
  steps,
  completedSteps,
  totalSteps,
  userAge,
  onAction,
  onOpenPlan,
  onEditPlan,
}: Props) {
  const t = useTranslations();
  const heroContent = {
    eyebrow: t("homepage.pre_cycle_egg_freezing.eyebrow"),
    title: t("homepage.pre_cycle_egg_freezing.title"),
    cycleStartLabel: t("homepage.pre_cycle_egg_freezing.cycle_start_label"),
    phaseDescription: t("homepage.pre_cycle_egg_freezing.phase_description"),
    intro: t("homepage.pre_cycle_egg_freezing.intro"),
    openPlanAccessibilityLabel: t(
      "homepage.pre_cycle_egg_freezing.open_plan_a11y",
    ),
    planAccessibilityLabel: t(
      "homepage.pre_cycle_egg_freezing.toggle_plan_a11y",
    ),
  };
  const sections: PreCycleHomeSection[] = [
    {
      id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.understand,
      progressStepIds: ["learn"],
      completionStepId: getJourneyStepCompletionId(
        "egg-freezing-solo",
        "learn",
      ),
      render: () => (
        <EggFreezingBiologyTimelineStepSectionCard
          userAge={userAge}
          onAction={onAction}
          showRail={false}
        />
      ),
    },
    {
      id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.knowYourNumbers,
      progressStepIds: ["test"],
      completionStepId: getJourneyStepCompletionId("egg-freezing-solo", "test"),
      render: () => (
        <EggFreezingKnowYourNumbersTimelineStepSectionCard
          onAction={onAction}
          showRail={false}
        />
      ),
    },
    {
      id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.findCare,
      progressStepIds: ["clinic"],
      completionStepId: getJourneyStepCompletionId(
        "egg-freezing-solo",
        "clinic",
      ),
      render: () => (
        <EggFreezingPrepTimelineRemainingSteps onAction={onAction} />
      ),
    },
    {
      id: EGG_FREEZING_PRE_CYCLE_SECTION_IDS.startCycle,
      includeInPlanSetup: false,
      progressStepIds: ["treatment"],
      render: () => (
        <EggFreezingPrepTimelineTransitionCards onAction={onAction} />
      ),
    },
  ];

  return (
    <PreCycleHome
      activeStep={activeStep}
      steps={steps}
      completedSteps={completedSteps}
      totalSteps={totalSteps}
      eyebrow={heroContent.eyebrow}
      title={heroContent.title}
      snapshotItems={[
        {
          label: t("homepage.pre_cycle_egg_freezing.next"),
          value:
            activeStep?.title ??
            t("homepage.pre_cycle_egg_freezing.choose_next_step"),
        },
        {
          label: t("homepage.pre_cycle_egg_freezing.treatment_cycle"),
          value: t("homepage.pre_cycle_egg_freezing.no_treatment_scheduled"),
          action: {
            label: t("homepage.pre_cycle_egg_freezing.add_treatment_dates"),
            signal: "open-start-treatment-modal",
            params: { presetCycleType: "egg_freezing" },
          },
        },
      ]}
      phaseDescription={heroContent.phaseDescription}
      intro={heroContent.intro}
      openPlanAccessibilityLabel={heroContent.openPlanAccessibilityLabel}
      planAccessibilityLabel={heroContent.planAccessibilityLabel}
      onAction={onAction}
      onOpenPlan={onOpenPlan}
      onEditPlan={onEditPlan}
      showStartCycleAction
      startCyclePreset="egg_freezing"
      sections={sections}
    />
  );
}
